import { useEffect, useMemo, useState } from "react";
import { Easing, useSharedValue, withTiming } from "react-native-reanimated";
import { useParams } from "solito/navigation";
import { usePathname } from "@medbillai/ui";
interface UseHeaderProgressBarProps {
  routePercentMap: Record<string, number>;
  routeNameMap?: Record<string, string>;
  defaultTitle?: string;
  initialProgress?: number;
}

/**
 * These maps should correspond with the routes the progress bar should be
 * animating EX: 
      export const routePercentMap: Record<string, number> = {
        [hipaaReleaseSelectPatient()]: 25,
        [hipaaReleasePatientInformation()]: 50,
        [hipaaReleaseDependentInformation()]: 75,
        [hipaaReleaseAdditionalInformation()]: 100,
      };
      export const routeNameMap: Record<string, string> = {
        [hipaaReleaseSelectPatient()]: "Patient Info",
        [hipaaReleasePatientInformation()]: "Patient data",
        [hipaaReleaseDependentInformation()]: "Patient Data",
        [hipaaReleaseAdditionalInformation()]: "Patient Data",
      };
 * If you would rather just pass in a title directly to progress bar 
 * that is fine as well, you do not have to use the title switching 
 * functionality provided here.
 */
export const useHeaderProgressBar = ({
  routePercentMap,
  routeNameMap,
  defaultTitle = "",
  initialProgress
}: UseHeaderProgressBarProps) => {
  const animatedProgress = useSharedValue(initialProgress ? initialProgress / 100 : 0);
  const pathname = usePathname();
  const [currentTitle, setCurrentTitle] = useState(routeNameMap ? routeNameMap[pathname] : defaultTitle);

  // TODO move into common hook that we can use for any flow screen in mobile or web
  const {
    step = ""
  } = useParams() ?? {};
  const stepPath = useMemo(() => {
    if (pathname.includes("[...step]")) {
      if (Array.isArray(step)) {
        return pathname.replace("[...step]", step.join("/"));
      }
      if (typeof step === "string") {
        return pathname.replace("[...step]", step);
      }
    }
    return undefined;
  }, [step, pathname]);
  useEffect(() => {
    const path = stepPath ?? pathname;
    const progressValue = pathname ? routePercentMap[path] ?? 0 : 0;
    // Animate to the new progress value outside of the render lifecycle of
    // the screen header
    animatedProgress.value = withTiming(progressValue / 100, {
      duration: 300,
      easing: Easing.bezier(0.65, 0, 0.35, 1)
    });
    if (routeNameMap) {
      if (routeNameMap[path]) {
        setCurrentTitle(routeNameMap[path]);
      } else {
        setCurrentTitle(defaultTitle);
      }
    } else if (currentTitle !== defaultTitle) {
      setCurrentTitle(defaultTitle);
    }
  }, [pathname, stepPath, animatedProgress, routePercentMap, routeNameMap, defaultTitle, currentTitle]);
  return {
    animatedProgress,
    currentTitle
  };
};